import { useCallback, useEffect, useRef, useState } from "react";

const isFileDrag = (event) => Array.from(event.dataTransfer?.types || []).includes("Files");

// The checklist keeps its own upload destinations. General dropping is limited
// to the actual form DOM, so portalled dialogs and navigation are excluded too.
export default function useKycDocumentDrop(onFiles) {
  const contentRef = useRef(null);
  const inputRef = useRef(null);
  const [dropBounds, setDropBounds] = useState(null);
  const openPicker = useCallback(() => inputRef.current?.click(), []);

  useEffect(() => {
    const reset = () => setDropBounds(null);
    const isGeneralTarget = (target) => target instanceof Element
      && contentRef.current?.contains(target)
      && !target.closest('#kyc-sec-docs, [role="dialog"], [aria-modal="true"]');
    const onDragOver = (event) => {
      if (!isFileDrag(event)) return;
      // Prevent the browser from navigating to a file on an unsupported area.
      // Do not stop propagation: dedicated upload handlers still run normally.
      event.preventDefault();
      if (!isGeneralTarget(event.target)) {
        reset();
        if (!event.target?.closest?.("#kyc-sec-docs")) event.dataTransfer.dropEffect = "none";
        return;
      }
      event.dataTransfer.dropEffect = "copy";
      // Keep the outline on the complete Client information card, regardless
      // of which ordinary section receives the drag.
      const area = contentRef.current.querySelector(".kycGroupInfo");
      if (!area) { reset(); return; }
      const rect = area.getBoundingClientRect();
      const visibleTop = Math.max(0, rect.top);
      const visibleBottom = Math.min(window.innerHeight, rect.bottom);
      const next = {
        top: rect.top, left: rect.left, width: rect.width, height: rect.height,
        "--drop-label-top": `${Math.max(0, (visibleTop + visibleBottom) / 2 - rect.top)}px`,
      };
      setDropBounds(prev => prev && Object.keys(next).every(key => prev[key] === next[key]) ? prev : next);
    };
    const onDrop = (event) => {
      reset();
      if (!isFileDrag(event) && !event.dataTransfer?.files?.length) return;
      event.preventDefault();
      if (isGeneralTarget(event.target) && event.dataTransfer.files.length) {
        onFiles(Array.from(event.dataTransfer.files));
      }
    };
    const onDragLeave = (event) => { if (!event.relatedTarget) reset(); };
    const onKeyDown = (event) => { if (event.key === "Escape") reset(); };
    document.addEventListener("dragover", onDragOver, true);
    document.addEventListener("drop", onDrop, true);
    document.addEventListener("dragleave", onDragLeave, true);
    document.addEventListener("dragend", reset, true);
    document.addEventListener("keydown", onKeyDown);
    window.addEventListener("blur", reset);
    window.addEventListener("scroll", reset, true);
    window.addEventListener("resize", reset);
    return () => {
      document.removeEventListener("dragover", onDragOver, true);
      document.removeEventListener("drop", onDrop, true);
      document.removeEventListener("dragleave", onDragLeave, true);
      document.removeEventListener("dragend", reset, true);
      document.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("blur", reset);
      window.removeEventListener("scroll", reset, true);
      window.removeEventListener("resize", reset);
    };
  }, [onFiles]);

  const onPickerChange = (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (files.length) onFiles(files);
  };
  return { contentRef, inputRef, openPicker, onPickerChange, dropBounds };
}
